import React from "react";
import { Button, Input } from "reactstrap";
import {
  QUESTION_TYPES,
  QUESTION_TYPE_OPTIONS,
  SHOW_QUESTION_TYPE_PICKER,
  STATUS,
  typeMeta,
} from "../constants";
import { Field } from "./Ui";

export const makeOption = (text = "", isCorrect = false) => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  option_text: text,
  is_correct: isCorrect,
});

/** A fresh, valid-by-default question draft. */
export const makeQuestion = (overrides = {}) => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  question_text: "",
  question_type: QUESTION_TYPES.MCQ_SINGLE,
  options: [makeOption(), makeOption()],
  correct_answer: "",
  explanation: "",
  difficulty_level: "medium",
  marks: 1,
  status: STATUS.ACTIVE,
  display_order: "",
  ...overrides,
});

/**
 * Rebuilds a draft's options when its type changes, so the shape always
 * matches what the new type expects.
 */
export const applyTypeChange = (question, nextType) => {
  const meta = typeMeta(nextType);
  const next = { ...question, question_type: nextType };

  if (!meta.hasOptions) {
    next.options = [];
    return next;
  }

  if (meta.fixedOptions) {
    next.options = meta.fixedOptions.map((text, i) => makeOption(text, i === 0));
    return next;
  }

  const existing = question.options || [];
  const options = existing.length >= meta.minOptions
    ? existing
    : [...existing, ...Array.from({ length: meta.minOptions - existing.length }, () => makeOption())];

  // Single-answer types must not carry more than one correct option over.
  if (!meta.multiCorrect) {
    let seenCorrect = false;
    next.options = options.map((o) => {
      if (o.is_correct && !seenCorrect) {
        seenCorrect = true;
        return o;
      }
      return { ...o, is_correct: false };
    });
    if (!seenCorrect && next.options.length) {
      next.options = next.options.map((o, i) => (i === 0 ? { ...o, is_correct: true } : o));
    }
  } else {
    next.options = options;
  }

  return next;
};

/**
 * Editor for a single question draft.
 * `compact` renders the tighter layout used by the bulk form.
 */
const QuestionEditor = ({ value, onChange, errors = {}, compact = false }) => {
  const meta = typeMeta(value.question_type);
  const readOnlyOptions = !!meta.fixedOptions;

  const set = (patch) => onChange({ ...value, ...patch });

  const setOption = (key, patch) =>
    set({
      options: value.options.map((o) => (o.key === key ? { ...o, ...patch } : o)),
    });

  const toggleCorrect = (key) => {
    if (meta.multiCorrect) {
      setOption(key, {
        is_correct: !value.options.find((o) => o.key === key)?.is_correct,
      });
    } else {
      // Radio behaviour - exactly one correct answer.
      set({
        options: value.options.map((o) => ({ ...o, is_correct: o.key === key })),
      });
    }
  };

  const addOption = () => set({ options: [...value.options, makeOption()] });

  const removeOption = (key) => {
    const remaining = value.options.filter((o) => o.key !== key);
    // Never drop below the type's minimum, and never leave zero correct answers.
    if (remaining.length < meta.minOptions) return;
    if (!remaining.some((o) => o.is_correct) && remaining.length) {
      remaining[0] = { ...remaining[0], is_correct: true };
    }
    set({ options: remaining });
  };

  return (
    <div className="qd-question-editor">
      <Field label="Question text" required error={errors.question_text}>
        <Input
          type="textarea"
          rows={compact ? 2 : 3}
          value={value.question_text}
          invalid={!!errors.question_text}
          placeholder="Type the question..."
          onChange={(e) => set({ question_text: e.target.value })}
        />
      </Field>

      {SHOW_QUESTION_TYPE_PICKER ? (
        <Field label="Question type" required error={errors.question_type}>
          <Input
            type="select"
            value={value.question_type}
            onChange={(e) => onChange(applyTypeChange(value, e.target.value))}
          >
            {QUESTION_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Input>
        </Field>
      ) : null}

      {meta.hasOptions ? (
        <div className="qd-options">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">
              Answer options <span className="text-danger">*</span>
              <span className="text-muted ms-2 small">
                {meta.multiCorrect
                  ? "Tick every correct answer"
                  : "Select the single correct answer"}
              </span>
            </label>
            {!readOnlyOptions ? (
              <Button size="sm" color="light" onClick={addOption} type="button">
                <i className="bx bx-plus me-1" />
                Add option
              </Button>
            ) : null}
          </div>

          {value.options.map((option) => (
            <div className="qd-option-row" key={option.key}>
              <Input
                type={meta.multiCorrect ? "checkbox" : "radio"}
                className="qd-option-check"
                checked={!!option.is_correct}
                onChange={() => toggleCorrect(option.key)}
                title="Mark as correct answer"
              />
              <Input
                type="text"
                value={option.option_text}
                readOnly={readOnlyOptions}
                placeholder="Option text"
                onChange={(e) =>
                  setOption(option.key, { option_text: e.target.value })
                }
              />
              {!readOnlyOptions ? (
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  title="Remove option"
                  disabled={value.options.length <= meta.minOptions}
                  onClick={() => removeOption(option.key)}
                >
                  <i className="bx bx-trash text-danger" />
                </Button>
              ) : null}
            </div>
          ))}

          {errors.options ? (
            <div className="text-danger small mt-1">{errors.options}</div>
          ) : null}
          {errors.correct ? (
            <div className="text-danger small mt-1">{errors.correct}</div>
          ) : null}
        </div>
      ) : null}

      {value.question_type === QUESTION_TYPES.SHORT_ANSWER ? (
        <Field
          label="Model answer"
          required
          error={errors.correct_answer}
          hint="Used as the reference answer when grading."
        >
          <Input
            type="text"
            value={value.correct_answer}
            invalid={!!errors.correct_answer}
            onChange={(e) => set({ correct_answer: e.target.value })}
          />
        </Field>
      ) : null}

      {value.question_type === QUESTION_TYPES.ESSAY ? (
        <Field label="Grading notes" hint="Optional guidance for the marker.">
          <Input
            type="textarea"
            rows={2}
            value={value.correct_answer}
            onChange={(e) => set({ correct_answer: e.target.value })}
          />
        </Field>
      ) : null}

      <Field label="Explanation" hint="Optional - shown to students after answering.">
        <Input
          type="textarea"
          rows={2}
          value={value.explanation}
          onChange={(e) => set({ explanation: e.target.value })}
        />
      </Field>
    </div>
  );
};

export default QuestionEditor;
